import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Container, Typography, Paper, Tabs, Tab, Grid, TextField, Button,
  Avatar, Alert, CircularProgress, Chip, IconButton, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Slider,
  Snackbar, InputAdornment, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import { useNavigate } from 'react-router-dom';
import {
  getMiPerfil, actualizarMiPerfil, subirMiFoto, eliminarMiFoto,
  cambiarMiPassword, getMiCarnet,
} from '../services/perfilApi';
import { getDepartamentos, getMunicipios } from '../services/ecolabApi';
import { isLogged, updateUserLocal } from '../services/authApi';
import { CarnetCardConSelector, descargarCarnetPng, imprimirCarnet } from '../components/DialogCarnet';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';

export default function MiPerfil() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('datos');
  const [perfil, setPerfil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });

  useEffect(() => {
    if (!isLogged()) { window.location.href = '/login'; return; }
    (async () => {
      try {
        const data = await getMiPerfil();
        setPerfil(data);
      } catch (err) {
        setError(err?.data?.mensaje || 'No se pudo cargar el perfil');
      } finally { setLoading(false); }
    })();
  }, []);

  const recargar = async () => {
    try {
      const data = await getMiPerfil();
      setPerfil(data);
    } catch {}
  };

  const showSnack = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', pt: 12 }}>
        <CircularProgress sx={{ color: '#2e7d32' }} />
      </Box>
    );
  }

  if (error || !perfil) {
    return (
      <Box sx={{ minHeight: '100vh', pt: 14, px: 3 }}>
        <Container maxWidth="md">
          <Alert severity="error">{error || 'No se pudo cargar el perfil'}</Alert>
        </Container>
      </Box>
    );
  }

  const initials = ((perfil.nombre?.[0] || '') + (perfil.apellido?.[0] || '')).toUpperCase();
  const fotoSrc = perfil.fotoUrl || undefined;
  const elegibleCarnet = perfil.cooperativaSiglas === 'FENAGRO APL' || perfil.cooperativaSiglas === 'CAPELLANES';

  return (
    <Box sx={{ minHeight: '100vh', background: '#f5f7f4', pt: { xs: 11, md: 13 }, pb: 8 }}>
      <Container maxWidth="lg">

        {/* Header */}
        <Paper sx={{
          p: { xs: 3, md: 4 }, mb: 3, borderRadius: 3, overflow: 'hidden',
          background: 'linear-gradient(135deg, #1a4d2e, #2e7d32)',
          color: '#fff', position: 'relative',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Avatar
              src={fotoSrc}
              sx={{
                width: { xs: 80, md: 100 }, height: { xs: 80, md: 100 },
                background: 'rgba(255,255,255,0.18)',
                fontSize: { xs: '1.8rem', md: '2.2rem' }, fontWeight: 700,
                border: '3px solid rgba(255,255,255,0.3)',
              }}
            >
              {initials}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                {perfil.nombre} {perfil.apellido}
              </Typography>
              <Typography sx={{ opacity: 0.9, fontSize: '0.95rem', mb: 1 }}>
                {perfil.email}
              </Typography>
              {perfil.cooperativaNombre && (
                <Chip
                  label={perfil.cooperativaNombre}
                  size="small"
                  sx={{ background: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 500 }}
                />
              )}
            </Box>
            <Button
              variant="outlined" startIcon={<AppsOutlinedIcon/>}
              onClick={() => navigate('/mi-portal')}
              sx={{
                color:'#fff', borderColor:'rgba(255,255,255,0.4)',
                '&:hover':{ borderColor:'#fff', background:'rgba(255,255,255,0.08)' },
                textTransform:'none', fontWeight:600,
              }}>
              Dashboard
            </Button>
          </Box>
        </Paper>

        {/* Tabs */}
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              background: '#fff',
              borderBottom: '1px solid #e0e0e0',
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.9rem', minHeight: 56 },
              '& .Mui-selected': { color: '#2e7d32 !important' },
            }}
            TabIndicatorProps={{ style: { background: '#2e7d32', height: 3 } }}
          >
            <Tab value="datos"    icon={<AccountCircleOutlinedIcon/>} iconPosition="start" label="Información personal" />
            <Tab value="foto"     icon={<PhotoCameraOutlinedIcon/>}   iconPosition="start" label="Foto de carnet" />
            {elegibleCarnet && (
              <Tab value="carnet" icon={<BadgeOutlinedIcon/>}         iconPosition="start" label="Mi carnet" />
            )}
            <Tab value="password" icon={<LockOutlinedIcon/>}          iconPosition="start" label="Cambiar contraseña" />
          </Tabs>

          <Box sx={{ p: { xs: 3, md: 4 } }}>
            {tab === 'datos'    && <TabDatos perfil={perfil} onSaved={recargar} showSnack={showSnack} />}
            {tab === 'foto'     && <TabFoto perfil={perfil} onChanged={recargar} showSnack={showSnack} />}
            {tab === 'carnet' && elegibleCarnet && <TabCarnet perfil={perfil} />}
            {tab === 'password' && <TabPassword showSnack={showSnack} />}
          </Box>
        </Paper>
      </Container>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Tab: Datos personales
// ─────────────────────────────────────────────────────────────────────
function TabDatos({ perfil, onSaved, showSnack }) {
  const [form, setForm] = useState({
    celular: perfil.celular || '',
    direccionTexto: perfil.direccionTexto || '',
    departamentoId: perfil.departamentoId || '',
    municipioId: perfil.municipioId || '',
  });
  const [saving, setSaving] = useState(false);
  const [departamentos, setDepartamentos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [loadingGeo, setLoadingGeo] = useState(false);

  // Cargar departamentos al montar (Colombia = 1045)
  useEffect(() => {
    setLoadingGeo(true);
    getDepartamentos(1045)
      .then(d => setDepartamentos(d || []))
      .catch(() => setDepartamentos([]))
      .finally(() => setLoadingGeo(false));
  }, []);

  // Cargar municipios cuando cambie el departamento
  useEffect(() => {
    if (!form.departamentoId) { setMunicipios([]); return; }
    setLoadingGeo(true);
    getMunicipios(form.departamentoId)
      .then(d => setMunicipios(d || []))
      .catch(() => setMunicipios([]))
      .finally(() => setLoadingGeo(false));
  }, [form.departamentoId]);

  const guardar = async () => {
    setSaving(true);
    try {
      await actualizarMiPerfil({
        celular: form.celular.trim(),
        direccionTexto: form.direccionTexto.trim(),
        departamentoId: form.departamentoId ? parseInt(form.departamentoId) : null,
        municipioId: form.municipioId ? parseInt(form.municipioId) : null,
      });
      showSnack('Perfil actualizado correctamente');
      onSaved();
    } catch (err) {
      showSnack(err?.data?.mensaje || 'No se pudo actualizar', 'error');
    } finally { setSaving(false); }
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3, fontSize: '0.84rem' }}>
        Los campos legales (nombre, apellido, documento, correo) solo pueden ser
        modificados por un administrador. Si necesita corregir alguno,
        contacte a la cooperativa.
      </Alert>

      <Grid container spacing={2.5}>
        {/* Solo lectura */}
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Nombre" value={perfil.nombre || ''} disabled />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Apellido" value={perfil.apellido || ''} disabled />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Correo electrónico" value={perfil.email || ''} disabled />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth size="small" label="Documento"
            value={`${perfil.tipoDocumento || ''} ${perfil.documento || ''}`}
            disabled
          />
        </Grid>

        <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

        {/* Editables */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth size="small" label="Celular"
            value={form.celular}
            onChange={e => setForm(p => ({ ...p, celular: e.target.value }))}
            InputProps={{ startAdornment: <InputAdornment position="start">+57</InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth size="small" label="Dirección"
            value={form.direccionTexto}
            onChange={e => setForm(p => ({ ...p, direccionTexto: e.target.value }))}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl size="small" fullWidth disabled={loadingGeo}>
            <InputLabel>{loadingGeo && !departamentos.length ? 'Cargando…' : 'Departamento'}</InputLabel>
            <Select
              value={form.departamentoId}
              label="Departamento"
              onChange={e => setForm(p => ({ ...p, departamentoId: e.target.value, municipioId: '' }))}
            >
              <MenuItem value=""><em>Seleccione…</em></MenuItem>
              {departamentos.map(d => (
                <MenuItem
                  key={d.DepartamentoID || d.departamentoID}
                  value={d.DepartamentoID || d.departamentoID}
                >
                  {d.Nombre || d.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl size="small" fullWidth disabled={!form.departamentoId || loadingGeo}>
            <InputLabel>{loadingGeo && form.departamentoId ? 'Cargando…' : 'Ciudad / Municipio'}</InputLabel>
            <Select
              value={form.municipioId}
              label="Ciudad / Municipio"
              onChange={e => setForm(p => ({ ...p, municipioId: e.target.value }))}
            >
              <MenuItem value=""><em>Seleccione…</em></MenuItem>
              {municipios.map(m => (
                <MenuItem
                  key={m.MunicipioID || m.municipioID}
                  value={m.MunicipioID || m.municipioID}
                >
                  {m.Nombre || m.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained" onClick={guardar} disabled={saving}
          sx={{ background: 'linear-gradient(135deg, #2e7d32, #43a047)', fontWeight: 700, px: 4 }}
        >
          {saving ? 'Guardando...' : 'Guardar cambios'}
        </Button>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Tab: Foto de carnet (upload + preview)
// ─────────────────────────────────────────────────────────────────────
function TabFoto({ perfil, onChanged, showSnack }) {
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropFile, setCropFile] = useState(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      showSnack('La foto no puede superar los 2 MB', 'error');
      return;
    }
    if (!['image/jpeg', 'image/png'].includes(f.type)) {
      showSnack('Solo se permiten JPG o PNG', 'error');
      return;
    }
    setCropFile(f);
    const reader = new FileReader();
    reader.onload = () => { setCropSrc(reader.result); setCropOpen(true); };
    reader.readAsDataURL(f);
  };

  const subir = async (blob) => {
    setBusy(true);
    try {
      const file = new File([blob], cropFile?.name || 'foto.jpg', { type: 'image/jpeg' });
      const r = await subirMiFoto(file);
      // Propagar fotoUrl al localStorage para que navbar y otros componentes refresquen
      updateUserLocal({ imagen: r.fotoUri, fotoUrl: r.fotoUrl });
      showSnack('Foto actualizada');
      onChanged();
    } catch (err) {
      showSnack(err?.data?.mensaje || 'No se pudo subir la foto', 'error');
    } finally {
      setBusy(false);
      setCropOpen(false);
      setCropSrc(null);
      setCropFile(null);
    }
  };

  const eliminar = async () => {
    if (!window.confirm('¿Eliminar foto de perfil?')) return;
    setBusy(true);
    try {
      await eliminarMiFoto();
      updateUserLocal({ imagen: null, fotoUrl: null });
      showSnack('Foto eliminada');
      onChanged();
    } catch (err) {
      showSnack(err?.data?.mensaje || 'Error', 'error');
    } finally { setBusy(false); }
  };

  const initials = ((perfil.nombre?.[0] || '') + (perfil.apellido?.[0] || '')).toUpperCase();

  return (
    <Box>
      <Alert severity="warning" sx={{ mb: 3, fontSize: '0.84rem' }}>
        <strong>Importante para el carnet:</strong> la foto debe ser en proporción
        <strong> 3:4 (formato cédula)</strong> con <strong>fondo blanco</strong> y
        rostro centrado. Si la foto no cumple este formato, podría verse mal
        ajustada o recortada en el carnet digital.
        <br/>
        Formato JPG o PNG, máximo 2 MB. El recortador integrado le ayudará a
        ajustarla a 3:4 antes de guardar.
      </Alert>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 4, flexWrap: 'wrap' }}>
        <Avatar
          src={perfil.fotoUrl || undefined}
          sx={{
            width: 180, height: 240, borderRadius: 3,
            background: 'linear-gradient(135deg, #2e7d32, #43a047)',
            color: '#fff', fontSize: '4rem', fontWeight: 700,
            border: '2px solid #e0e0e0',
          }}
          variant="rounded"
        >
          {initials}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography variant="h6" sx={{ color: '#1a3a1e', fontWeight: 700, mb: 1 }}>
            {perfil.fotoUrl ? 'Cambiar foto' : 'Subir foto'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
            {perfil.fotoUrl
              ? 'Su foto actual se muestra a la izquierda. Puede cambiarla o eliminarla.'
              : 'Aún no ha subido una foto. Suba una imagen clara de su rostro.'}
          </Typography>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png"
            onChange={onFile}
            style={{ display: 'none' }}
          />

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<PhotoCameraOutlinedIcon/>}
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
              sx={{ background: 'linear-gradient(135deg, #2e7d32, #43a047)', fontWeight: 700 }}
            >
              {perfil.fotoUrl ? 'Cambiar foto' : 'Subir foto'}
            </Button>
            {perfil.fotoUrl && (
              <Button
                variant="outlined" color="error"
                startIcon={<DeleteOutlineIcon/>}
                onClick={eliminar} disabled={busy}
              >
                Eliminar
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      <CropDialog
        open={cropOpen}
        src={cropSrc}
        onClose={() => { setCropOpen(false); setCropSrc(null); setCropFile(null); }}
        onConfirm={subir}
        busy={busy}
      />
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Crop dialog — canvas simple sin librería externa
// ─────────────────────────────────────────────────────────────────────
function CropDialog({ open, src, onClose, onConfirm, busy }) {
  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, ox: 0, oy: 0 });

  // Tamaño del visor 3:4 (300x400 visual)
  const W = 300, H = 400;

  const onMouseDown = (e) => {
    setDragging(true);
    const clientX = e.touches?.[0]?.clientX ?? e.clientX;
    const clientY = e.touches?.[0]?.clientY ?? e.clientY;
    dragStartRef.current = { x: clientX, y: clientY, ox: offset.x, oy: offset.y };
  };

  const onMouseMove = (e) => {
    if (!dragging) return;
    const clientX = e.touches?.[0]?.clientX ?? e.clientX;
    const clientY = e.touches?.[0]?.clientY ?? e.clientY;
    const dx = clientX - dragStartRef.current.x;
    const dy = clientY - dragStartRef.current.y;
    setOffset({ x: dragStartRef.current.ox + dx, y: dragStartRef.current.oy + dy });
  };

  const onMouseUp = () => setDragging(false);

  useEffect(() => {
    if (open) { setZoom(1); setOffset({ x: 0, y: 0 }); }
  }, [open]);

  const exportar = async () => {
    const img = imgRef.current;
    if (!img) return;
    const canvas = canvasRef.current || document.createElement('canvas');
    canvas.width = 600;  // export 600x800 (3:4)
    canvas.height = 800;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Calcular dimensiones de la imagen visualmente
    const imgNatural = { w: img.naturalWidth, h: img.naturalHeight };
    // Cálculo: contain en el visor con zoom y offset
    const scale = Math.min(W / imgNatural.w, H / imgNatural.h) * zoom;
    const drawW = imgNatural.w * scale;
    const drawH = imgNatural.h * scale;
    const cx = W / 2 + offset.x;
    const cy = H / 2 + offset.y;
    // Coordenadas en el visor
    const drawX = cx - drawW / 2;
    const drawY = cy - drawH / 2;

    // Mapeo del visor (W,H) al canvas (600,800): factor 2
    ctx.drawImage(img, drawX * 2, drawY * 2, drawW * 2, drawH * 2);
    canvas.toBlob((blob) => { if (blob) onConfirm(blob); }, 'image/jpeg', 0.92);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ background: 'linear-gradient(135deg, #1a4d2e, #2e7d32)', color: '#fff' }}>
        Ajustar foto del carnet (3:4)
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
          Arrastre para mover y use el deslizador para acercar/alejar.
        </Typography>
        <Box
          sx={{
            position: 'relative', width: W, height: H, mx: 'auto', mt: 2,
            background: '#f0f0f0', overflow: 'hidden', border: '2px solid #2e7d32',
            borderRadius: 1, cursor: dragging ? 'grabbing' : 'grab',
            userSelect: 'none', touchAction: 'none',
          }}
          onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
          onTouchStart={onMouseDown} onTouchMove={onMouseMove} onTouchEnd={onMouseUp}
        >
          {src && (
            <img
              ref={imgRef}
              src={src}
              alt=""
              draggable={false}
              style={{
                position: 'absolute',
                top: '50%', left: '50%',
                transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) scale(${zoom})`,
                maxWidth: W, maxHeight: H, pointerEvents: 'none',
              }}
            />
          )}
        </Box>
        <Box sx={{ px: 2, mt: 3 }}>
          <Typography variant="caption" sx={{ color: '#666' }}>Zoom</Typography>
          <Slider
            value={zoom}
            min={0.5} max={3} step={0.05}
            onChange={(_, v) => setZoom(v)}
            sx={{ color: '#2e7d32' }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancelar</Button>
        <Button
          variant="contained" onClick={exportar} disabled={busy}
          sx={{ background: 'linear-gradient(135deg, #2e7d32, #43a047)', fontWeight: 700 }}
        >
          {busy ? 'Subiendo...' : 'Guardar foto'}
        </Button>
      </DialogActions>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Tab: Mi carnet — render inline
// ─────────────────────────────────────────────────────────────────────
function TabCarnet({ perfil }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const cardRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const d = await getMiCarnet();
        setData(d);
      } catch {} finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress sx={{ color: '#2e7d32' }} /></Box>;

  if (!data?.tieneCarnet) {
    return (
      <Alert severity="info" sx={{ fontSize: '0.9rem' }}>
        <strong>Aún no se ha generado su carnet.</strong><br/>
        Contacte a un administrador de su cooperativa para que emita su carnet digital.
        Una vez emitido podrá verlo y descargarlo desde aquí.
      </Alert>
    );
  }

  return (
    <Box>
      <Alert severity="success" icon={<CheckCircleIcon/>} sx={{ mb: 2 }}>
        Su carnet está <strong>{data.carnet.estado}</strong>. Vigencia hasta el{' '}
        {new Date(data.carnet.fechaVigencia).toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })}.
      </Alert>

      <Alert severity="info" sx={{ mb: 3, fontSize: '0.82rem' }}>
        Si su foto no se ve bien ajustada en el carnet (especialmente en la
        versión "Original" de Capellanías), vaya a la pestaña{' '}
        <strong>"Foto de carnet"</strong> y vuelva a subirla en
        proporción <strong>3:4 con fondo blanco</strong>.
      </Alert>

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <CarnetCardConSelector
          carnet={data.carnet}
          onCardRef={(el) => { cardRef.current = el; }}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5 }}>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon/>}
          onClick={() => descargarCarnetPng(cardRef.current, data.carnet.codigo)}
          sx={{ color: '#2e7d32', borderColor: '#2e7d32', fontWeight: 600 }}
        >
          Descargar PNG
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon/>}
          onClick={() => imprimirCarnet(cardRef.current, data.carnet.codigo)}
          sx={{ background: 'linear-gradient(135deg, #2e7d32, #43a047)', fontWeight: 700 }}
        >
          Imprimir
        </Button>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Tab: Cambiar contraseña
// ─────────────────────────────────────────────────────────────────────
function TabPassword({ showSnack }) {
  const [form, setForm] = useState({ actual: '', nueva: '', confirmar: '' });
  const [show, setShow] = useState({ a: false, n: false, c: false });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (form.nueva.length < 8) { showSnack('La nueva contraseña debe tener al menos 8 caracteres', 'error'); return; }
    if (form.nueva !== form.confirmar) { showSnack('Las contraseñas no coinciden', 'error'); return; }
    setBusy(true);
    try {
      await cambiarMiPassword(form.actual, form.nueva);
      showSnack('Contraseña actualizada');
      setForm({ actual: '', nueva: '', confirmar: '' });
    } catch (err) {
      showSnack(err?.data?.mensaje || 'No se pudo cambiar', 'error');
    } finally { setBusy(false); }
  };

  const tField = (key, label, k) => (
    <TextField
      fullWidth size="small" label={label}
      type={show[k] ? 'text' : 'password'}
      value={form[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton size="small" onClick={() => setShow(s => ({ ...s, [k]: !s[k] }))}>
              {show[k] ? <VisibilityOffIcon fontSize="small"/> : <VisibilityIcon fontSize="small"/>}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );

  return (
    <Box sx={{ maxWidth: 460 }}>
      <Alert severity="info" sx={{ mb: 3, fontSize: '0.84rem' }}>
        La contraseña nueva debe tener al menos 8 caracteres.
      </Alert>
      <Grid container spacing={2.5}>
        <Grid item xs={12}>{tField('actual',    'Contraseña actual',     'a')}</Grid>
        <Grid item xs={12}>{tField('nueva',     'Nueva contraseña',      'n')}</Grid>
        <Grid item xs={12}>{tField('confirmar', 'Confirmar contraseña',  'c')}</Grid>
      </Grid>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained" onClick={submit} disabled={busy || !form.actual || !form.nueva}
          sx={{ background: 'linear-gradient(135deg, #2e7d32, #43a047)', fontWeight: 700, px: 4 }}
        >
          {busy ? 'Cambiando...' : 'Cambiar contraseña'}
        </Button>
      </Box>
    </Box>
  );
}
